import classNames from 'classnames';
import { useRouter } from 'next/router';

import { useRouteContext } from 'src/hooks/useRouteContext';

import { ComponentProps } from 'src/types/component-props';
import { CoreGlobalNavigationHeaderData } from 'src/types/components';

import Image from 'components/BaseHelpers/Image';
import Link from 'components/BaseHelpers/Link';
import AncillaryNavigation from 'components/Core/CoreNavigationHeader/components/AncillaryNavigation';
import OtherCollegeNavigation from 'components/Core/CoreNavigationHeader/components/OtherCollegeNavigation';

import DesktopPrimaryNav from './components/DesktopPrimaryNav';
import MobilePrimaryNav from './components/MobilePrimaryNav';

import styles from './CoreNavigationHeader.module.scss';

export type CoreNavigationHeaderProps = ComponentProps & CoreGlobalNavigationHeaderData;

const CoreNavigationHeader = ({ fields }: CoreNavigationHeaderProps): JSX.Element => {
  const { primaryNavigationItems, ancillaryNavigation, logo, otherCollegeNavigation } = fields;
  const { routeData } = useRouteContext();

  const router = useRouter();
  const isHomePage = router.asPath === '/';

  const updatedPrimaryNavigationItems: typeof primaryNavigationItems = isHomePage
    ? [
        {
          label: 'College of Health Sciences',
          itemType: 'page',
          page: {
            label: 'College of Health Sciences',
            href: '/college',
          },
        },
        {
          label: 'School of Medicine',
          itemType: 'page',
          page: {
            label: 'School of Medicine',
            target: '_blank',
            href: 'https://www.slhn.org/school-of-medicine',
          },
        },
        {
          label: 'School of Nursing',
          itemType: 'page',
          page: {
            label: 'School of Nursing',
            target: '_blank',
            href: 'https://www.slhn.org/school-of-nursing',
          },
        },
        {
          label: 'Graduate Medical Education',
          itemType: 'page',
          page: {
            label: 'Graduate Medical Education',
            target: '_blank',
            href: 'https://www.slhn.org/graduate-medical-education',
          },
        },
        {
          label: 'Additional Programs',
          itemType: 'page',
          page: {
            label: 'Additional Programs',
            target: '_blank',
            href: 'https://www.slhn.org/med-ed/programs-resources',
          },
        },
      ]
    : primaryNavigationItems;

  const updatedAncillaryNavigation = isHomePage
    ? {
        links: [
          {
            label: 'Careers',
            href: 'https://www.slhn.org/careers',
            target: '_blank',
          },
        ],
      }
    : ancillaryNavigation;

  const updatedLogo = isHomePage
    ? {
        src: '/images/SLUHN_blue.svg',
        alt: '',
      }
    : logo;

  return (
    <header className={styles.main}>
      <a href="#main" className={styles['skip-link']}>
        {'Skip to main content'}
      </a>
      <div className={styles.topNavBarWrapper}>
        <div className={classNames(styles.topNavBar, 'global__wrapper--1920')}>
          <OtherCollegeNavigation
            className={styles.otherCollegeNavigation}
            {...otherCollegeNavigation}
          />
          <AncillaryNavigation
            className={styles.ancillaryNavigation}
            {...updatedAncillaryNavigation}
          />
        </div>
      </div>
      <div className={styles.mainNavBarWrapper}>
        <div className={classNames(styles.mainNavBar, 'global__wrapper--1920')}>
          {updatedLogo &&
            (routeData?.route.path === '/' ? (
              <h1
                aria-label="St Lukes College of Health Sciences"
                className={styles['img-container']}
              >
                <Image fill={true} {...updatedLogo} />
              </h1>
            ) : (
              <Link
                href="/"
                aria-label="Go to the St Lukes homepage"
                className={styles['img-container']}
              >
                <Image fill={true} {...updatedLogo} />
              </Link>
            ))}
          <DesktopPrimaryNav navigationItems={updatedPrimaryNavigationItems} />
          <MobilePrimaryNav
            ancillaryNavigationItems={updatedAncillaryNavigation}
            primaryNavigationItems={updatedPrimaryNavigationItems}
          />
        </div>
      </div>
    </header>
  );
};

export default CoreNavigationHeader;
